import { forwardRef } from 'react';
import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { TextField, type SxProps, type Theme } from '@mui/material';
import { IMaskInput } from 'react-imask';

// NINO format: 2 letters + 6 digits + 1 letter
// Display format: "PE 63 54 34 C"
const NINO_MASK = 'aa 00 00 00 a';

const NINO_DEFINITIONS = {
  a: /[A-Za-z]/,
  0: /[0-9]/,
};

interface NinoMaskedInputProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const NinoMaskedInput = forwardRef<HTMLInputElement, NinoMaskedInputProps>(
  function NinoMaskedInput(props, ref) {
    const { onChange, ...other } = props;
    return (
      <IMaskInput
        {...other}
        mask={NINO_MASK}
        definitions={NINO_DEFINITIONS}
        prepare={(value: string) => value.toUpperCase()}
        inputRef={ref}
        onAccept={(value: string) =>
          onChange({ target: { name: props.name, value } })
        }
        overwrite
      />
    );
  }
);

/**
 * Converts a displayed NINO value (with spaces) to raw format (no spaces).
 * @example ninoToRaw('PE 63 54 34 C') // 'PE635434C'
 */
export const ninoToRaw = (displayValue: string): string =>
  displayValue.replace(/[\s-]/g, '').toUpperCase();

/**
 * Converts a raw NINO value to display format (with spaces).
 * @example ninoToDisplay('PE635434C') // 'PE 63 54 34 C'
 */
export const ninoToDisplay = (rawValue: string): string => {
  if (!rawValue) return '';
  const clean = rawValue.toUpperCase().replace(/[\s-]/g, '');
  if (clean.length !== 9) return rawValue;
  return `${clean.slice(0, 2)} ${clean.slice(2, 4)} ${clean.slice(4, 6)} ${clean.slice(6, 8)} ${clean.slice(8)}`;
};

export interface RHFNinoInputProps<
  T extends FieldValues,
  TTransformedValues extends FieldValues | undefined = undefined
> {
  /** Field name - must match form schema */
  name: FieldPath<T>;
  /** React Hook Form control object */
  control: Control<T, any, TTransformedValues>;
  /** Field label */
  label?: string;
  /** Helper text to display below the input */
  helperText?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Full width of parent container */
  fullWidth?: boolean;
  /** MUI sx prop for custom styling */
  sx?: SxProps<Theme>;
  /** Data test ID for testing */
  'data-testid'?: string;
}

/**
 * National Insurance Number input with masking, integrated with React Hook Form.
 * Displays as "PE 63 54 34 C" but stores raw value "PE635434C".
 *
 * @example
 * ```tsx
 * <RHFNinoInput
 *   name="soleTrader.nationalInsuranceNumber"
 *   control={form.control}
 *   label="National Insurance Number"
 *   required
 * />
 * ```
 */
export const RHFNinoInput = <T extends FieldValues, TTransformedValues extends FieldValues | undefined = undefined>({
  name,
  control,
  label,
  helperText,
  disabled,
  required,
  fullWidth,
  sx,
  'data-testid': testId,
}: RHFNinoInputProps<T, TTransformedValues>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { ref, value, onChange, onBlur },
        fieldState: { error },
      }) => (
        <TextField
          value={ninoToDisplay(value ?? '')}
          onChange={e => onChange(ninoToRaw(e.target.value))}
          onBlur={onBlur}
          inputRef={ref}
          label={label}
          disabled={disabled}
          required={required}
          fullWidth={fullWidth}
          error={!!error}
          helperText={error?.message || helperText}
          sx={sx}
          slotProps={{
            input: {
              inputComponent: NinoMaskedInput as never,
            },
            htmlInput: {
              'data-testid': testId,
              style: { textTransform: 'uppercase' },
              autoCapitalize: 'characters',
            },
          }}
        />
      )}
    />
  );
};
